import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gauge, ImagePlus, Search, Trash2, Upload } from "lucide-react";
import { rhm, apiError } from "../api";
import { Btn, Empty, Field, Input, Modal, Select } from "../ui";

const FOLDERS = ["All", "Blog", "Homepage", "Products", "Consultation", "Banners", "General", "Team", "Testimonials"];

// One shared media library — used by the Website Layout builder and the Blog CMS.
export const MediaLibrary = ({ onPick, onClose, folder: initialFolder = "Blog", canDelete = true }) => {
    const qc = useQueryClient();
    const fileRef = useRef(null);
    const [folder, setFolder] = useState(initialFolder);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(null);
    const [cutout, setCutout] = useState(false);

    const { data: media = [] } = useQuery({
        queryKey: ["media", folder, q],
        queryFn: async () => (await rhm.get(
            `/site/media?folder=${folder === "All" ? "" : folder}&q=${encodeURIComponent(q)}`)).data,
    });

    const upload = async (files) => {
        setBusy(true);
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                await rhm.post(
                    `/site/media?folder=${folder === "All" ? "Blog" : folder}&cutout=${cutout ? "true" : "false"}`, fd);
            }
            toast.success(files.length > 1 ? `${files.length} images uploaded` : "Image uploaded");
            qc.invalidateQueries({ queryKey: ["media"] });
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const saveMeta = async (item, patch) => {
        try {
            await rhm.patch(`/site/media/${item.id}`, patch);
            setSelected((cur) => (cur && cur.id === item.id ? { ...cur, ...patch } : cur));
            qc.invalidateQueries({ queryKey: ["media"] });
            toast.success("Image details saved");
        } catch (e) { toast.error(apiError(e)); }
    };

    const remove = async (item) => {
        if (!window.confirm("Delete this image from the library?")) return;
        try {
            await rhm.delete(`/site/media/${item.id}`);
            setSelected(null);
            qc.invalidateQueries({ queryKey: ["media"] });
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Modal open onClose={onClose} wide title="Media library" testId="media-library">
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <Field label="Folder" className="w-40">
                    <Select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="media-folder"
                        options={FOLDERS.map((f) => ({ value: f, label: f }))} />
                </Field>
                <Field label="Search" className="flex-1 min-w-[180px]">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"
                            placeholder="File name or ALT text" data-testid="media-search" />
                    </div>
                </Field>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="media-file-input"
                    onChange={(e) => upload([...e.target.files])} />
                <label className="flex items-center gap-2 text-xs text-zinc-400 pb-3 cursor-pointer"
                    title="Cuts a plain white or near-white background away so the artwork sits on the page with nothing behind it.">
                    <input type="checkbox" checked={cutout} data-testid="media-cutout"
                        onChange={(e) => setCutout(e.target.checked)} />
                    Transparent background
                </label>
                <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-upload">
                    <Upload className="w-3.5 h-3.5 mr-1 inline" /> {busy ? "UPLOADING…" : "UPLOAD"}
                </Btn>
                <Btn variant="ghost" disabled={busy} data-testid="media-optimise"
                    title="Re-saves every image as WebP so pages load faster. Links stay the same."
                    onClick={async () => {
                        if (!window.confirm("Re-save every image in the library as WebP? Links stay the same.")) return;
                        setBusy(true);
                        try {
                            const { data } = await rhm.post("/site/media/optimise");
                            toast.success(`${data.converted} images optimised — ${data.saved_kb} KB saved`);
                            qc.invalidateQueries({ queryKey: ["media"] });
                        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
                    }}>
                    <Gauge className="w-3.5 h-3.5 mr-1 inline" /> OPTIMISE ALL
                </Btn>
            </div>

            {!media.length && <Empty msg="No images in this folder yet. Upload one to get started." />}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="media-grid">
                {media.map((m) => (
                    <button key={m.id} onClick={() => setSelected(m)} data-testid={`media-item-${m.id}`}
                        className={`text-left border transition-colors ${
                            selected?.id === m.id ? "border-gold" : "border-white/10 hover:border-white/40"}`}>
                        <div className="aspect-[4/3] bg-bg0 overflow-hidden">
                            <img src={m.url} alt={m.alt || m.filename} loading="lazy" className="w-full h-full object-cover" />
                        </div>
                        <div className="p-2">
                            <div className="text-xs truncate">{m.filename}</div>
                            <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                {m.alt ? "ALT SET" : "NO ALT TEXT"}
                            </div>
                        </div>
                    </button>
                ))}
            </div>

            {selected && (
                <div className="mt-5 border-t border-white/10 pt-5 grid md:grid-cols-2 gap-4" data-testid="media-detail">
                    <img src={selected.url} alt={selected.alt || ""} className="w-full max-h-56 object-contain bg-bg0" />
                    <div className="space-y-3">
                        <Field label="ALT text" hint="Describes the image for search engines and screen readers">
                            <Input defaultValue={selected.alt} data-testid="media-alt"
                                onBlur={(e) => saveMeta(selected, { alt: e.target.value })} />
                        </Field>
                        <Field label="Caption">
                            <Input defaultValue={selected.caption} data-testid="media-caption"
                                onBlur={(e) => saveMeta(selected, { caption: e.target.value })} />
                        </Field>
                        <Field label="File name">
                            <Input defaultValue={selected.filename} data-testid="media-filename"
                                onBlur={(e) => saveMeta(selected, { filename: e.target.value })} />
                        </Field>
                        <div className="flex items-center justify-between gap-3 pt-1">
                            {canDelete ? (
                                <button onClick={() => remove(selected)} data-testid="media-delete"
                                    className="mono text-[10px] tracking-[0.16em] text-red-400 inline-flex items-center gap-2">
                                    <Trash2 className="w-3.5 h-3.5" /> DELETE
                                </button>
                            ) : <span />}
                            <Btn data-testid="media-use"
                                onClick={() => onPick?.({ url: selected.url, alt: selected.alt || "",
                                    caption: selected.caption || "", media_id: selected.id })}>
                                <ImagePlus className="w-3.5 h-3.5 mr-1 inline" /> USE THIS IMAGE
                            </Btn>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export const ImageField = ({ value = {}, onChange, label = "Image", testId = "image-field", folder = "Blog" }) => {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={testId}>
            <div className="mono text-[10px] tracking-[0.18em] text-zinc-400 mb-1.5">{label}</div>
            {value.url ? (
                <div className="border border-white/10">
                    <img src={value.url} alt={value.alt || ""} className="w-full h-32 object-cover" />
                    <div className="p-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-zinc-500 truncate">{value.alt || "No ALT text"}</span>
                        <div className="flex gap-2">
                            <button onClick={() => setOpen(true)} data-testid={`${testId}-change`}
                                className="mono text-[9px] tracking-[0.16em] text-gold">CHANGE</button>
                            <button onClick={() => onChange({ url: "", alt: "", caption: "" })}
                                data-testid={`${testId}-remove`}
                                className="mono text-[9px] tracking-[0.16em] text-zinc-500 hover:text-red-400">REMOVE</button>
                        </div>
                    </div>
                </div>
            ) : (
                <button onClick={() => setOpen(true)} data-testid={`${testId}-pick`}
                    className="w-full border border-dashed border-white/20 py-6 text-center text-sm text-zinc-400 hover:border-gold hover:text-gold">
                    <ImagePlus className="w-4 h-4 mx-auto mb-1" /> Choose or upload an image
                </button>
            )}
            {value.url !== undefined && value.url !== "" && (
                <div className="mt-2 space-y-2">
                    <Input value={value.alt || ""} placeholder="ALT text" data-testid={`${testId}-alt`}
                        onChange={(e) => onChange({ ...value, alt: e.target.value })} />
                    <Input value={value.caption || ""} placeholder="Caption (optional)" data-testid={`${testId}-caption`}
                        onChange={(e) => onChange({ ...value, caption: e.target.value })} />
                </div>
            )}
            {open && (
                <MediaLibrary folder={folder} onClose={() => setOpen(false)}
                    onPick={(picked) => { onChange({ ...value, ...picked }); setOpen(false); }} />
            )}
        </div>
    );
};
